// consumerLoan

import {
  Box,
  Button,
  Card,
  Group,
  Input,
  rem,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { toCurrency } from "utils/common";
import { useEffect, useState } from "react";
import { TermLoanDetail } from "./components/TermLoanDetail";
import CurrencyInput from "components/common/currency-input/CurrencyInput";
import { LoanPending } from "./components/LoanPending";
import test from "assets/images/pngs/test1.png";
import { LoanCompleted, LoanDetailInfo } from "./components/StepLoanCompleted";
import { useLoanType } from "context/LoanTypeContext";
import OTpCode from "components/modal/otp-code/OtpCode";
import i18n from "i18n/i18n";

const schema = yup.object().shape({
  // VersionEn: schema yup ở mức module — thông báo lỗi được tính khi dựng schema, không
  // phải lúc render, nên dùng i18n.t() để mỗi lần nạp lấy đúng ngôn ngữ hiện tại.
  loanAmount: yup.string().required(`${i18n.t("PLEASE_ENTER_LOAN_AMOUNT")}`),
  methodInterest: yup
    .string()
    .required(`${i18n.t("PLEASE_ENTER_INTEREST_METHOD")}`),
});

export const ConsumerLoan = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const { loanType } = useLoanType();

  const form = useForm({
    initialValues: {
      loanType: t(loanType),
      maxLoanAmount: toCurrency(100000000),
      loanAmount: "",
      loanTerm: "",
      disbursementAccount: "",
      methodInterest: "",
    },
    validate: yupResolver(schema),
  });
  useEffect(() => {
    form.setFieldValue("loanType", t(loanType));
  }, [loanType, form, t]);
  const [opened, { open: OpenOTPCode, close: CloseOTPCode }] =
    useDisclosure(false);

  const nextStep = () => setStep((prev) => Math.min(prev + 1, 3));
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 0));

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <Box>
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                borderLeft: "3px solid #246CF9",
                paddingLeft: "10px",
                margin: "20px 0",
              }}
            >
              <Text fw={600} size="24px">
                {t("LOAN_APPLICATION")}
              </Text>
            </Box>

            <Group align="start" style={{ gap: "32px" }}>
              <Card
                shadow="sm"
                radius="md"
                sx={{
                  flex: "1",
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  flexWrap: "nowrap",
                  width: "100%",
                }}
              >
                <form
                  onSubmit={form.onSubmit(nextStep)}
                  style={{ display: "flex", flexDirection: "column", flex: 1 }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Card.Section inheritPadding py="xs">
                      <Text fz="lg" weight={600}>
                        {t("REGISTRATION_INFORMATION")}
                      </Text>
                    </Card.Section>

                    <Input.Wrapper label={t("LOAN_CURRENCY")} mb="lg">
                      <Input
                        classNames={{
                          input: "bg-[#D2D2D2] text-black",
                        }}
                        {...form.getInputProps("loanType")}
                        disabled
                      />
                    </Input.Wrapper>

                    <Input.Wrapper label={t("MAXIMUM_LOAN_AMOUNT")} mb="lg">
                      <Input
                        rightSection={
                          <Text fw={400} fz={14} mr={10}>
                            VND
                          </Text>
                        }
                        classNames={{
                          input: "bg-[#D2D2D2] text-black",
                        }}
                        {...form.getInputProps("maxLoanAmount")}
                        disabled
                      />
                    </Input.Wrapper>

                    <Input.Wrapper label={t("LOAN_AMOUNT")} mb="lg">
                      <CurrencyInput
                        placeholder={`${t("PLACEHOLDER_LOAN_AMOUNT")}`}
                        type="text"
                        {...form.getInputProps("loanAmount")}
                      />
                    </Input.Wrapper>

                    <Select
                      label={t("LOAN_TERM")}
                      data={[
                        {
                          label: t("6_MONTHS_-_10%/YEAR") as string,
                          value: "1",
                        },
                      ]}
                      defaultValue="1"
                      rightSection={<IconChevronDown size="1rem" />}
                      mb="lg"
                    />

                    <Select
                      label={t("DISBURSEMENT_ACCOUNT")}
                      placeholder={t("SELECT_DISBURSEMENT_ACCOUNT") as string}
                      data={[
                        {
                          label: t("6_MONTHS_-_10%/YEAR") as string,
                          value: "1",
                        },
                      ]}
                      rightSection={<IconChevronDown size="1rem" />}
                      mb="lg"
                    />

                    <Input.Wrapper
                      label={t("INTEREST_CALCULATION_METHOD")}
                      mb="lg"
                    >
                      <Input
                        placeholder={t("EQUAL_INSTALLMENTS") as string}
                        {...form.getInputProps("methodInterest")}
                      />
                    </Input.Wrapper>
                  </Box>
                  {/* Footer luôn ở cuối */}
                  <Group position="center" mt={rem(20)}>
                    <Box
                      style={{
                        position: "fixed",
                        bottom: 0,
                        right: 0,
                        width: "calc(100% - var(--mantine-navbar-width, 0px))",
                        padding: "10px 16px",
                        display: "flex",
                        justifyContent: "right",
                        gap: "10px",
                        boxShadow: "0px 5px 15px 0px #00000026",
                        height: "76px",
                        alignItems: "center",
                        backgroundColor: "white",
                        zIndex: 1,
                      }}
                    >
                      <Button
                        sx={() => ({
                          width: "160px",
                          background: "#F4F4F6",
                          borderRadius: "8px",
                          padding: "8px 10px",
                          cursor: "pointer",
                          fontWeight: 600,
                          color: "black",
                          ":hover": {
                            background: "#F4F4F6",
                            color: "black",
                          },
                        })}
                        mr="xs"
                        type="reset"
                        onClick={prevStep}
                      >
                        {t("CANCEL")}
                      </Button>
                      <Button
                        sx={() => ({
                          width: "160px",
                          color: "white",
                          borderRadius: "8px",
                          padding: "8px 10px",
                          cursor: "pointer",
                          fontWeight: 400,
                          ":hover": {
                            color: "white",
                          },
                          background: "#2A81D0",
                        })}
                        type="submit"
                      >
                        {t("NEXT")}
                      </Button>
                    </Box>
                  </Group>
                </form>
              </Card>

              <Card radius="md" p={20} maw={400} w="100%" mah={197}>
                <Stack spacing="sm">
                  <Text fw={700} fz={16}>
                    {t("LOAN_ESTIMATE")}
                  </Text>
                  <Group position="apart">
                    <Text fw={400} fz={14}>
                      {t("MONTHLY_INTEREST_RATE")}
                    </Text>
                    <Text fz={16} fw={600}>
                      1.00%
                    </Text>
                  </Group>
                  <Group position="apart">
                    <Text fw={400} fz={14}>
                      {t("EARLY_SETTLEMENT_FEE")}
                    </Text>
                    <Text fz={16} fw={600}>
                      3.00%
                    </Text>
                  </Group>
                  <Group position="apart">
                    <Text fw={400} fz={14}>
                      {t("MONTHLY_PAYMENT_AMOUNT")}
                    </Text>
                    <Text fz={16} fw={600} c="#0047D0">
                      {toCurrency(1045000)} đ
                    </Text>
                  </Group>
                </Stack>
              </Card>
            </Group>
          </Box>
        );
      case 2:
        return (
          <>
            <TermLoanDetail onNext={() => OpenOTPCode()} onCancel={prevStep} />
            <OTpCode
              opened={opened}
              close={CloseOTPCode}
              setShowSuccessModal={nextStep}
            />
          </>
        );
      case 3:
        return (
          <LoanPending
            goBack={prevStep}
            form={
              <LoanCompleted
                title={t("ORDER_CREATED_SUCCESSFULLY_AND_WAITING_FOR_APPROVAL")}
                amount={toCurrency(100000000)}
                img={test}
                form={
                  <LoanDetailInfo
                    details={[
                      { label: t("REFERENCE_CODE"), content: "DF330035253231" },
                      {
                        label: t("DEPOSIT_AMOUNT"),
                        content: "150,000,000 VND",
                      },
                      {
                        label: t("TERM_TYPE"),
                        content: t("6_MONTHS_-_10%/YEAR"),
                      },
                      {
                        label: t("EXPECTED_PROFIT_AT_THE_END_OF_THE_PERIOD"),
                        content: "105,000,000 VND",
                      },
                      {
                        label: t("FORM_OF_RENEWAL_OF_PRINCIPAL_AND_INTEREST"),
                        content: t("REVOLVING_PRINCIPAL_INTEREST"),
                      },
                      { label: t("CREATOR"), content: "Duyna" },
                      {
                        label: t("DATE_CREATED"),
                        content: "26/03/2024  15:30",
                      },
                    ]}
                  />
                }
                addNew={() => {
                  setStep(0);
                }}
              />
            }
          />
        );
      case 0:
      default:
        return (
          <Box>
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                borderLeft: "3px solid #246CF9",
                paddingLeft: "10px",
                margin: "20px 0",
              }}
            >
              <Text fw={600} size="24px">
                {t("LOAN_APPLICATION")}
              </Text>
            </Box>

            <Card
              shadow="sm"
              radius="md"
              maw={700}
              sx={{
                flex: "1",
                padding: "20px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <form
                onSubmit={form.onSubmit(nextStep)}
                style={{ display: "flex", flexDirection: "column", flex: 1 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Card.Section inheritPadding py="xs">
                    <Text fz="lg" weight={600}>
                      {t("REGISTRATION_INFORMATION")}
                    </Text>
                  </Card.Section>

                  <Input.Wrapper label={t("LOAN_CURRENCY")} mb="lg">
                    <Input
                      classNames={{
                        input: "bg-[#D2D2D2] text-black",
                      }}
                      {...form.getInputProps("loanType")}
                      disabled
                    />
                  </Input.Wrapper>

                  <Input.Wrapper label={t("MAXIMUM_LOAN_AMOUNT")} mb="lg">
                    <Input
                      rightSection={
                        <Text fw={400} fz={14} mr={10}>
                          VND
                        </Text>
                      }
                      classNames={{
                        input: "bg-[#D2D2D2] text-black",
                      }}
                      {...form.getInputProps("maxLoanAmount")}
                      disabled
                    />
                  </Input.Wrapper>

                  <Input.Wrapper label={t("LOAN_AMOUNT")} mb="lg">
                    <CurrencyInput
                      placeholder={`${t("PLACEHOLDER_LOAN_AMOUNT")}`}
                      type="text"
                      {...form.getInputProps("loanAmount")}
                    />
                  </Input.Wrapper>

                  <Select
                    label={t("LOAN_TERM")}
                    data={[
                      {
                        label: t("6_MONTHS_-_10%/YEAR") as string,
                        value: "1",
                      },
                    ]}
                    defaultValue="1"
                    rightSection={<IconChevronDown size="1rem" />}
                    mb="lg"
                  />

                  <Select
                    label={t("DISBURSEMENT_ACCOUNT")}
                    placeholder={t("SELECT_DISBURSEMENT_ACCOUNT") as string}
                    data={[
                      {
                        label: t("6_MONTHS_-_10%/YEAR") as string,
                        value: "1",
                      },
                    ]}
                    rightSection={<IconChevronDown size="1rem" />}
                    mb="lg"
                  />

                  <Input.Wrapper
                    label={t("INTEREST_CALCULATION_METHOD")}
                    mb="lg"
                  >
                    <Input
                      placeholder={t("EQUAL_INSTALLMENTS") as string}
                      {...form.getInputProps("methodInterest")}
                    />
                  </Input.Wrapper>
                </Box>
                <Group position="center" mt={rem(20)}>
                  <Box
                    style={{
                      position: "fixed",
                      bottom: 0,
                      right: 0,
                      width: "calc(100% - var(--mantine-navbar-width, 0px))",
                      padding: "10px 16px",
                      display: "flex",
                      justifyContent: "right",
                      gap: "10px",
                      boxShadow: "0px 5px 15px 0px #00000026",
                      height: "76px",
                      alignItems: "center",
                      backgroundColor: "white",
                      zIndex: 1,
                    }}
                  >
                    <Button
                      sx={() => ({
                        width: "160px",
                        background: "#F4F4F6",
                        borderRadius: "8px",
                        padding: "8px 10px",
                        cursor: "pointer",
                        fontWeight: 600,
                        color: "black",
                        ":hover": {
                          background: "#F4F4F6",
                          color: "black",
                        },
                      })}
                      mr="xs"
                      type="reset"
                      // onClick={(e) => {
                      //   e.preventDefault();
                      // }}
                      onClick={prevStep}
                      disabled={step === 0}
                    >
                      {t("CANCEL")}
                    </Button>
                    <Button
                      sx={() => ({
                        width: "160px",
                        color: "white",
                        borderRadius: "8px",
                        padding: "8px 10px",
                        cursor: "pointer",
                        fontWeight: 400,
                        ":hover": {
                          color: "white",
                        },
                        background: "#2A81D0",
                      })}
                      type="submit"
                    >
                      {t("NEXT")}
                    </Button>
                  </Box>
                </Group>
              </form>
            </Card>
          </Box>
        );
    }
  };

  return <Box>{renderStepContent()}</Box>;
};
